import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "../../../../../../components/ui/dialog";
import { ArrowLeft, ChevronUp, ChevronDown, Columns, Edit, Hash, Target, Clock, Gauge, Activity, MapPin, Ruler, Users, FileText, Wrench, MessageSquare, CheckCircle, AlertCircle, FileSearch, Pencil, Download, Trash2, Upload, Plus, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '../../../../../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../../../../components/ui/tabs';
import type { WeldData as WeldDataTableWeldData } from '../../../../../../components/WeldDataTable';
import { AddRepairLogDialog, type RepairLogData } from './AddRepairLogDialog';
import { UploadNDTRepairLogDataDialog } from './UploadNDTRepairLogDataDialog';
import { ColumnSelectorModal, type ColumnDefinition } from '../../../../../../components/ui/ColumnSelectorModal';
import { DocumentUploadModal } from '../DocumentUploadModal';
import { DocumentDownloadModal } from '../DocumentDownloadModal';
import {
  fetchNDTIndicationLogs,
  createNDTRepairLog,
  updateNDTRepairLog,
  deleteNDTRepairLog,
  fetchNDTRepairLogDoc,
  uploadNDTRepairLogDoc
} from '../../../../../../redux/ProductionData/actions';
import { deleteSpecificationDoc } from '../../../../../../redux/Project/specificationSaga';
import { getListMaterialCategory } from '../../../../../../redux/Project/materialSaga';
import { SPEC_TYPE_LABELS, SPEC_TAG_TYPES } from '../../../../constants/specificationConstants';
import { DeleteConfirmationDialog } from '../../../../../../components/ui/DeleteConfirmationDialog';

// Column definitions for NDT Repair Log Table
const NDT_REPAIR_LOG_COLUMNS: ColumnDefinition[] = [
  { key: 'sno', label: 'Sno', category: 'Basic Info' },
  { key: 'date', label: 'Date', category: 'Basic Info' },
  { key: 'shift', label: 'Shift', category: 'Basic Info' },
  { key: 'oclockLocation', label: "o'clock Location", category: 'Basic Info' },
  { key: 'actions', label: 'Actions', category: 'Actions' },
  { key: 'defectLocationStart', label: 'Defect Location Start', category: 'Defect Info' },
  { key: 'defectLocationEnd', label: 'Defect Location End', category: 'Defect Info' },
  { key: 'defectLength', label: 'Defect Length', category: 'Defect Info' },
  { key: 'defectDepth', label: 'Defect Depth', category: 'Defect Info' },
  { key: 'defectHeight', label: 'Defect Height', category: 'Defect Info' },
  { key: 'weldersN', label: 'Welders N.', category: 'Welder Info' },
  { key: 'welderRepNo', label: 'Welder Rep. No', category: 'Welder Info' },
  { key: 'location', label: 'Location', category: 'Basic Info' },
  { key: 'defectType', label: 'Defect type', category: 'Defect Info' },
  { key: 'passName', label: 'Pass Name', category: 'Defect Info' },
  { key: 'time', label: 'Time', category: 'Basic Info' },
  { key: 'evaluation', label: 'Evaluation', category: 'Repair Info' },
  { key: 'criticalWeld', label: 'Critical Weld', category: 'Repair Info' },
  { key: 'repair', label: 'Repair', category: 'Repair Info' },
  { key: 'comments', label: 'Comments', category: 'Repair Info' },
  { key: 'correctiveActions', label: 'Corrective Actions', category: 'Repair Info' },
  { key: 'acceptedWorkmanship', label: 'Accepted Workmanship', category: 'Repair Info' },
  // { key: 'rca', label: 'RCA', category: 'Repair Info' },
  { key: 'rootCause', label: 'Root cause', category: 'Repair Info' },
  { key: 'Documents', label: 'Documents', category: 'Documents' },
];

// Helper function to get all column keys
const getAllColumnKeys = (columns: ColumnDefinition[]): string[] => {
  return columns.map(col => col.key);
};

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  /* For Firefox */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #f1f1f1;
  }

  /* Smooth scrolling */
  .scrollbar-thin {
    scroll-behavior: smooth;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    .mobile-scroll-container {
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
  }
`;

interface NdtRepairLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  weld: WeldDataTableWeldData;
  canEdit?: boolean;
}

// Sorting types
interface Sorting {
  field: string;
  order: 'ascend' | 'descend';
}

export const NdtRepairLogModal: React.FC<NdtRepairLogModalProps> = ({
  isOpen,
  onClose,
  weld,
  canEdit = true
}) => {
  const dispatch = useDispatch();
  const {
    ndtIndicationLogs,
    ndtIndicationLogsLoading,
    createNDTRepairLogLoading,
    updateNDTRepairLogLoading,
    deleteNDTRepairLogLoading,
    deleteNDTRepairLogSuccess,
    ndtRepairLogDocs,
    ndtRepairLogDocsLoading,
    uploadNDTRepairLogDocLoading,
    uploadNDTRepairLogDocSuccess
  } = useSelector((state: any) => state.productionData);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<any>(null);
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [sorting, setSorting] = useState<Sorting | null>(null);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('indication');
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDeleteRepairLogModalVisible, setIsDeleteRepairLogModalVisible] = useState(false);
  const [repairLogToDelete, setRepairLogToDelete] = useState<any>(null);
  const [isUploadNDTRepairLogDataDialogOpen, setIsUploadNDTRepairLogDataDialogOpen] = useState(false);
  const [isUploadManualWeldDataDialogOpen, setIsUploadManualWeldDataDialogOpen] = useState(false);

  // Column visibility state - initialize with all columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    getAllColumnKeys(NDT_REPAIR_LOG_COLUMNS)
  );

  // NDT Tags state
  const [localDocumentTags, setLocalDocumentTags] = useState<any[]>([]);

  // Fetch NDT indication logs when modal opens
  useEffect(() => {
    if (isOpen && weld) {
      //console.log('weld', weld);
      const weldId = weld.weldNumber
      const projectId = weld.projectId || (weld as any).project?.projectId;
      const pipeId = weld.selectedPipe?.pipeId ? Number(weld.selectedPipe?.pipeId) : null;

      if (projectId && weldId) {
        const payload: any = {
          project_id: projectId,
          weld_id: weldId
        };

        // Only include pipe_id if it's a valid number
        if (pipeId !== null && pipeId !== undefined && !isNaN(pipeId)) {
          payload.pipe_id = pipeId;
        }

        dispatch(fetchNDTIndicationLogs(payload));
        dispatch(fetchNDTRepairLogDoc(payload));
      }
    }
  }, [isOpen, weld, dispatch]);

  // Load NDT tags when modal opens
  useEffect(() => {
    const loadTags = async () => {
      if (isOpen) {
        try {
          const allTags = await getListMaterialCategory();
          if (allTags && allTags.length > 0) {
            setLocalDocumentTags(allTags);
          }
        } catch (error) {
          console.error('Failed to load NDT tags:', error);
        }
      }
    };

    loadTags();
  }, [isOpen]);

  // Convert NDT tags to options for the MultiSelect component
  const ndtTagOptions = useMemo(() => {
    const tagType = SPEC_TAG_TYPES['NDT'];
    if (!tagType) return [];

    const ndtTags = localDocumentTags.filter((tag: any) => tag.tags_type === tagType);
    return ndtTags.map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    }));
  }, [localDocumentTags]);



  // Refresh documents after successful upload
  useEffect(() => {
    if (uploadNDTRepairLogDocSuccess && !uploadNDTRepairLogDocLoading && isOpen && weld) {
      const weldId = typeof weld.id === 'number' ? weld.id : (weld.id ? parseInt(String(weld.id), 10) : 0);
      const projectId = weld.projectId || (weld as any).project?.projectId;

      if (projectId && weldId) {
        dispatch(fetchNDTRepairLogDoc({
          project_id: projectId,
          weld_id: weldId
        }));
      }
    }
  }, [uploadNDTRepairLogDocSuccess, uploadNDTRepairLogDocLoading, isOpen, weld, dispatch]);

  // Refresh logs after successful deletion
  useEffect(() => {
    if (deleteNDTRepairLogSuccess && !deleteNDTRepairLogLoading && isOpen && weld) {
      const weldId = typeof weld.id === 'number' ? weld.id : (weld.id ? parseInt(String(weld.id), 10) : 0);
      const projectId = weld.projectId || (weld as any).project?.projectId;
      const pipeId = weld.selectedPipe?.pipeId ? Number(weld.selectedPipe?.pipeId) : null;

      if (projectId && weldId) {
        const payload: any = {
          project_id: projectId,
          weld_id: weldId
        };

        if (pipeId !== null && pipeId !== undefined && !isNaN(pipeId)) {
          payload.pipe_id = pipeId;
        }

        dispatch(fetchNDTIndicationLogs(payload));
        setIsDeleteRepairLogModalVisible(false);
        setRepairLogToDelete(null);
      }
    }
  }, [deleteNDTRepairLogSuccess, deleteNDTRepairLogLoading, isOpen, weld, dispatch]);

  // Helper function to check if a column is visible
  const isColumnVisible = useCallback((columnKey: string): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return true; // Show all columns if no visibility specified
    }
    return visibleColumns.includes(columnKey);
  }, [visibleColumns]);

  // Helper function to calculate sticky left position for a column
  const getStickyLeft = useCallback((columnKey: string): string => {
    const stickyColumnOrder = ['sno', 'date', 'shift', 'oclockLocation', 'Documents', 'actions'];
    const stickyColumnWidths: Record<string, number> = {
      'sno': 60 + 32, // min-w-[60px] + padding (px-4 = 16px each side = 32px)
      'date': 100 + 32, // min-w-[100px] + padding
      'shift': 100 + 32, // min-w-[100px] + padding
      'oclockLocation': 120 + 32, // min-w-[120px] + padding
      'Documents': 100 + 32, // min-w-[100px] + padding
      'actions': 80 + 32 // min-w-[80px] + padding
    };

    let left = 0;
    const currentIndex = stickyColumnOrder.indexOf(columnKey);
    for (let i = 0; i < currentIndex; i++) {
      if (isColumnVisible(stickyColumnOrder[i])) {
        left += stickyColumnWidths[stickyColumnOrder[i]];
      }
    }
    return `${left}px`;
  }, [isColumnVisible]);

  // Column visibility handler
  const handleColumnsSave = useCallback((selectedColumns: string[]) => {
    setVisibleColumns(selectedColumns);
  }, []);

  // Sorting function
  const handleSort = (field: string) => {
    if (sorting?.field === field) {
      setSorting(sorting.order === 'ascend' ? { field, order: 'descend' } : null);
    } else {
      setSorting({ field, order: 'ascend' });
    }
  };

  // Render sort icon
  const renderSortIcon = (field: string) => {
    if (sorting?.field !== field) {
      return <ChevronUp className="h-3 w-3 text-gray-400" />;
    }
    return sorting.order === 'ascend' ?
      <ChevronUp className="h-3 w-3 text-blue-600" /> :
      <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  // Helper function to parse defect location string "833, 919" into start and end
  const parseDefectLocation = (defectLocation: string | undefined | null) => {
    if (!defectLocation || typeof defectLocation !== 'string') {
      return {
        start: '',
        end: ''
      };
    }
    const parts = defectLocation.split(',').map(s => s.trim());
    return {
      start: parts[0] || '',
      end: parts[1] || ''
    };
  };

  // Transform document data from Redux state
  const documentData = useMemo(() => {
    if (!ndtRepairLogDocs || !Array.isArray(ndtRepairLogDocs) || ndtRepairLogDocs.length === 0) {
      return [];
    }
    return ndtRepairLogDocs
  }, [ndtRepairLogDocs]);

  // Handlers for document actions
  const handleDownloadDocument = (doc: any) => {
    if (doc && doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    } else if (doc && doc.s3Key) {
      // If only s3Key is available, construct URL or call API
      console.log('Download document with s3Key:', doc.s3Key);
    }
  };

  const handleDeleteDocument = (document: any) => {
    setDocumentToDelete(document);
    setIsDeleteModalVisible(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    setDeleteLoading(true);
    try {
      console.log('documentToDelete', documentToDelete);
      // Get docId from the document - it might be in different fields
      // NDT documents typically have docId or id field
      const docId = documentToDelete.doc_id;

      if (!docId) {
        console.error('Document ID not found', documentToDelete);
        setDeleteLoading(false);
        return;
      }

      const docIds = [Number(docId)];
      const documentType = 'NDT';
      const typeLabel = 'NDT Document';

      const deleteResponse = await deleteSpecificationDoc(
        docIds,
        documentToDelete,
        typeLabel
      );

      if (deleteResponse) {
        // Refresh documents after successful deletion
        const weldId = typeof weld.id === 'number' ? weld.id : (weld.id ? parseInt(String(weld.id), 10) : 0);
        const projectId = weld.projectId || (weld as any).project?.projectId;
        const pipeId = weld.selectedPipe?.pipeId ? Number(weld.selectedPipe?.pipeId) : null;

        if (projectId && weldId) {
          const payload: any = {
            id: Number(documentData[0].id),
            project_id: projectId,
            weld_id: weldId,
            ndt_indication_doc_id: documentData[0].ndtIndicationDocId.filter((id: number) => id !== docId)
          };


          // Refresh NDT repair log documents
          await dispatch(fetchNDTRepairLogDoc(payload));

          // Also refresh NDT indication logs to update the document list
          await dispatch(fetchNDTIndicationLogs(payload));
        }

        // Close the delete modal after successful deletion
        handleCancelDelete();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handler for document upload
  const handleUpload = async (response: any) => {
    try {
      const weldId = typeof weld.id === 'number' ? weld.id : (weld.id ? parseInt(String(weld.id), 10) : 0);
      const projectId = weld.projectId || (weld as any).project?.projectId;
      const isUpdate = documentData?.length > 0 && documentData[0].ndtIndicationDocId.length > 0 ? true : false;

      if (projectId && weldId && response) {
        // console.log('response', response);
        // console.log('documentData', documentData);
        let docIds = response.map((item: any) => Number(item.docId));
        if (documentData?.length > 0 && documentData[0]?.ndtIndicationDocId?.length > 0) {
          docIds = [...documentData[0].ndtIndicationDocId, ...docIds];
        }

        const data: any = {
          project_id: projectId,
          pipe_id: weld.selectedPipe?.pipeId ? Number(weld.selectedPipe?.pipeId) : null,
          weld_id: weldId,
          ndt_indication_doc_id: docIds,
          wps_number: weld.wpsNumber || '',
          pipe_dia: weld.selectedPipe?.pipeDiameter || null
        }
        if (isUpdate) {
          data.id = Number(documentData[0].id);
        }
        const payload: any = {

          items: [data]
        };

        await dispatch(uploadNDTRepairLogDoc(payload));
        // dispatch(fetchNDTRepairLogDoc({
        //   project_id: projectId,
        //   weld_id: weldId
        // }));
      }
      setIsUploadDocumentModalOpen(false);
    } catch (error) {
      console.error('Error uploading document:', error);
      setIsUploadDocumentModalOpen(false);
    }
  };

  // Handler for opening document download modal
  const handleOpenDocuments = (documents: any[]) => {
    if (documents && documents.length > 0) {
      setSelectedDocuments(documents);
      setIsDownloadModalOpen(true);
    }
  };

  // Transform API data to match component format
  const repairLogData = useMemo(() => {
    // Ensure ndtIndicationLogs is an array
    if (!ndtIndicationLogs || !Array.isArray(ndtIndicationLogs) || ndtIndicationLogs.length === 0) {
      return [];
    }

    // Add weldJointNumber to each log based on weld prop
    return ndtIndicationLogs.map((log: any) => ({
      ...log,
      weldJointNumber: weld.weldNumber || (weld.id ? String(weld.id) : '')
    }));
  }, [ndtIndicationLogs, weld]);

  // Color palette for badges (same as WeldPassDetailModal)
  const colorPalette = [
    'bg-green-200', 'bg-blue-100', 'bg-green-100', 'bg-red-100',
    'bg-cyan-100', 'bg-teal-100', 'bg-indigo-100', 'bg-lime-100',
    'bg-pink-100', 'bg-rose-100', 'bg-emerald-100', 'bg-violet-100',
    'bg-amber-100', 'bg-sky-100', 'bg-stone-100', 'bg-slate-100',
    'bg-zinc-100', 'bg-purple-100', 'bg-orange-100', 'bg-yellow-100'
  ];

  // Create details array with weld information for badges
  const details: Array<{ label: string; value: string | number }> = [
    { label: 'Weld ID', value: weld.weldNumber || weld.id || '-' },
    { label: 'WPS Number', value: weld.wpsNumber || '-' },
    { label: 'Pipe Diameter', value: '20' },
    { label: 'Pipe Size', value: '11.5' }
  ];

  // Convert details to infoItems with colors
  const infoItems = details.map((item, index) => ({
    ...item,
    bgColor: colorPalette[index % colorPalette.length]
  }));

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7);

  // Apply sorting to data
  const sortedData = useMemo(() => {
    if (!sorting) return repairLogData;

    return [...repairLogData].sort((a, b) => {
      let aValue: any = a[sorting.field as keyof typeof a];
      let bValue: any = b[sorting.field as keyof typeof b];

      // Handle numeric values (extract numbers from strings like "86 mm")
      if (sorting.field === 'defectLength' || sorting.field === 'defectDepth' || sorting.field === 'defectHeight') {
        const aNum = parseFloat(aValue?.toString().replace(/[^\d.-]/g, '') || '0');
        const bNum = parseFloat(bValue?.toString().replace(/[^\d.-]/g, '') || '0');
        aValue = aNum;
        bValue = bNum;
      }

      // Handle defect location start/end (extract from defectLocation string)
      if (sorting.field === 'defectLocationStart' || sorting.field === 'defectLocationEnd') {
        const aParts = parseDefectLocation(a.defectLocation);
        const bParts = parseDefectLocation(b.defectLocation);
        const aNum = parseFloat(sorting.field === 'defectLocationStart' ? aParts.start : aParts.end || '0');
        const bNum = parseFloat(sorting.field === 'defectLocationStart' ? bParts.start : bParts.end || '0');
        aValue = aNum;
        bValue = bNum;
      }

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sorting.order === 'ascend' ? -1 : 1;
      if (aValue > bValue) return sorting.order === 'ascend' ? 1 : -1;
      return 0;
    });
  }, [repairLogData, sorting]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0  [&>button]:hidden overflow-hidden ">
        <div className="flex flex-col h-full">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold">NDT Indication Log </h1>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {visibleItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                    {infoItems.length > 7 && (
                      <button
                        onClick={() => setShowAllInfo(!showAllInfo)}
                        className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                        <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button
                onClick={() => setIsUploadNDTRepairLogDataDialogOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center w-full sm:w-auto"
              >
                <Upload className="h-4 w-4" />
                Upload NDT Repair Log Data
              </Button>
              <Button
                onClick={() => setIsUploadManualWeldDataDialogOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center w-full sm:w-auto"
              >
                <Upload className="h-4 w-4" />
                Upload Manual Weld Data
              </Button>
              {canEdit && (
                <>
                  
                  <Button
                    onClick={() => setIsUploadDocumentModalOpen(true)}
                    className="flex items-center gap-2"
                    title="Upload Document"
                  //   variant="outline"
                  >
                    <Upload className="h-4 w-4" />
                    <span className="hidden sm:inline">Upload Document</span>
                  </Button>
                  <Button
                    variant="default"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => setIsAddDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> Add Repair Log
                  </Button>
                </>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Inject custom scrollbar styles */}
            <style>{scrollbarStyles}</style>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              {/* Tabs List */}
              <div className="border-b border-gray-200 px-6 flex-shrink-0">
                <TabsList className="h-auto w-full justify-start gap-2 border-b bg-transparent px-0 py-2">
                  <TabsTrigger
                    value="indication"
                    className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-4 pb-3 pt-2 text-sm font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[2px]"
                  >
                    Indication
                  </TabsTrigger>
                  <TabsTrigger
                    value="document"
                    className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-4 pb-3 pt-2 text-sm font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[2px]"
                  >
                    Document
                  </TabsTrigger>
                </TabsList>
              </div>
              {/* Indication Tab */}
              <TabsContent 
                value="indication" 
                className="flex-1 m-0 p-0 data-[state=active]:flex data-[state=inactive]:hidden flex-col"
                style={{ height: 'calc(100% - 72px)' }}
              >
                <div className="flex-1 relative">
                  {ndtIndicationLogsLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                        <span className="text-gray-600 text-lg">Loading repair log data...</span>
                      </div>
                    </div>
                  ) : sortedData.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-gray-500 text-center">
                        <p>No repair log data available</p>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="absolute inset-0 overflow-x-auto overflow-y-auto pb-20 scrollbar-thin"
                      style={{ 
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehaviorX: 'contain'
                      }}
                    >
                        <div className="bg-white border border-gray-200">
                          {/* Single unified table with native HTML elements */}
                          <table className="border-collapse w-full" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
                          <thead className="sticky top-0 bg-white z-30">
                            <tr className="border-b h-[72px] bg-gray-50">
                              {isColumnVisible('sno') && (
                                <th className="sticky left-0 z-20 bg-gray-50 min-w-[60px] w-[92px] px-4 text-center text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                  <div className="flex items-center justify-center gap-2">
                                    <Hash className="h-3 w-3" />
                                    <span className="text-xs">Sno</span>
                                  </div>
                                </th>
                              )}
                              {isColumnVisible('date') && (
                                <th className="sticky z-20 bg-gray-50 min-w-[100px] w-[132px] px-4 text-center text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('date') }}>
                                  <button
                                    onClick={() => handleSort('date')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Clock className="h-3 w-3" />
                                    <span className="text-xs">Date</span>
                                    {renderSortIcon('date')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('shift') && (
                                <th className="sticky z-20 bg-gray-50 min-w-[100px] w-[132px] px-4 text-center text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('shift') }}>
                                  <button
                                    onClick={() => handleSort('shift')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Activity className="h-3 w-3" />
                                    <span className="text-xs">Shift</span>
                                    {renderSortIcon('shift')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('oclockLocation') && (
                                <th className="sticky z-20 bg-gray-50 min-w-[120px] w-[152px] px-4 text-center text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('oclockLocation') }}>
                                  <button
                                    onClick={() => handleSort('oclockLocation')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Gauge className="h-3 w-3" />
                                    <span className="text-xs">o'clock Location</span>
                                    {renderSortIcon('oclockLocation')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('Documents') && (
                                <th className="sticky z-20 bg-gray-50 min-w-[100px] w-[132px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('Documents') }}>
                                  <div className="flex items-center justify-center gap-2" title="Documents column - not sortable">
                                    <FileText className="h-3 w-3" />
                                    <span className="text-xs">Documents</span>
                                  </div>
                                </th>
                              )}
                              {isColumnVisible('actions') && (
                                <th className="sticky z-20 bg-gray-50 min-w-[80px] w-[112px] px-4 text-center text-xs font-medium text-gray-600" style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                                  <div className="flex items-center justify-center gap-2" title="Action column - not sortable">
                                    {/* <Pencil className="h-4 w-4" /> */}
                                    <span className="text-xs">Actions</span>
                                  </div>
                                </th>
                              )}
                              {isColumnVisible('defectLocationStart') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('defectLocationStart')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <MapPin className="h-3 w-3" />
                                    <span className="text-xs">Defect Location Start</span>
                                    {renderSortIcon('defectLocationStart')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('defectLocationEnd') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('defectLocationEnd')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <MapPin className="h-3 w-3" />
                                    <span className="text-xs">Defect Location End</span>
                                    {renderSortIcon('defectLocationEnd')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('defectLength') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('defectLength')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Ruler className="h-3 w-3" />
                                    <span className="text-xs">Defect Length</span>
                                    {renderSortIcon('defectLength')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('defectDepth') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('defectDepth')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Ruler className="h-3 w-3" />
                                    <span className="text-xs">Defect Depth</span>
                                    {renderSortIcon('defectDepth')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('defectHeight') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('defectHeight')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Ruler className="h-3 w-3" />
                                    <span className="text-xs">Defect Height</span>
                                    {renderSortIcon('defectHeight')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('weldersN') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('weldersN')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Users className="h-3 w-3" />
                                    <span className="text-xs">Welders N.</span>
                                    {renderSortIcon('weldersN')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('welderRepNo') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('welderRepNo')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Users className="h-3 w-3" />
                                    <span className="text-xs">Welder Rep. No</span>
                                    {renderSortIcon('welderRepNo')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('location') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('location')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <MapPin className="h-3 w-3" />
                                    <span className="text-xs">Location</span>
                                    {renderSortIcon('location')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('defectType') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('defectType')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <AlertCircle className="h-3 w-3" />
                                    <span className="text-xs">Defect type</span>
                                    {renderSortIcon('defectType')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('passName') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('passName')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <AlertCircle className="h-3 w-3" />
                                    <span className="text-xs">Pass Name</span>
                                    {renderSortIcon('passName')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('time') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('time')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Clock className="h-3 w-3" />
                                    <span className="text-xs">Time</span>
                                    {renderSortIcon('time')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('evaluation') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('evaluation')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                    <span className="text-xs">Evaluation</span>
                                    {renderSortIcon('evaluation')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('criticalWeld') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('criticalWeld')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Target className="h-3 w-3" />
                                    <span className="text-xs">Critical Weld</span>
                                    {renderSortIcon('criticalWeld')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('repair') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('repair')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Wrench className="h-3 w-3" />
                                    <span className="text-xs">Repair</span>
                                    {renderSortIcon('repair')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('comments') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('comments')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <MessageSquare className="h-3 w-3" />
                                    <span className="text-xs">Comments</span>
                                    {renderSortIcon('comments')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('correctiveActions') && (
                                <th className="px-4 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors" style={{ maxWidth: '400px', width: '400px' }}>
                                  <button
                                    onClick={() => handleSort('correctiveActions')}
                                    className="flex items-center justify-start gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <Wrench className="h-3 w-3" />
                                    <span className="text-xs">Corrective Actions</span>
                                    {renderSortIcon('correctiveActions')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('acceptedWorkmanship') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('acceptedWorkmanship')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                    <span className="text-xs">Accepted workmanship</span>
                                    {renderSortIcon('acceptedWorkmanship')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('rca') && (
                                <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors">
                                  <button
                                    onClick={() => handleSort('rca')}
                                    className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <FileText className="h-3 w-3" />
                                    <span className="text-xs">RCA</span>
                                    {renderSortIcon('rca')}
                                  </button>
                                </th>
                              )}
                              {isColumnVisible('rootCause') && (
                                <th className="px-4 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors" style={{ maxWidth: '600px', width: '600px' }}>
                                  <button
                                    onClick={() => handleSort('rootCause')}
                                    className="flex items-center justify-start gap-2 hover:text-gray-800 transition-colors w-full"
                                  >
                                    <FileSearch className="h-3 w-3" />
                                    <span className="text-xs">Root cause</span>
                                    {renderSortIcon('rootCause')}
                                  </button>
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {ndtIndicationLogsLoading ? (
                              <tr>
                                <td colSpan={visibleColumns.length || 20} className="px-4 py-8 text-center text-gray-500">
                                  Loading repair log data...
                                </td>
                              </tr>
                            ) : (
                              sortedData.map((row: any, index: number) => {
                                const defectLocationParts = parseDefectLocation(row.defectLocation);
                                return (
                                  <tr key={row.id || index} className="h-[52px] bg-white hover:bg-gray-50 transition-colors border-b border-gray-200">
                                    {isColumnVisible('sno') && (
                                      <td className="sticky z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('sno') }}>{index + 1}</td>
                                    )}
                                    {isColumnVisible('date') && (
                                      <td className="sticky z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('date') }}>{row.date || '-'}</td>
                                    )}
                                    {isColumnVisible('shift') && (
                                      <td className="sticky z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('shift') }}>{row.shift}</td>
                                    )}
                                    {isColumnVisible('oclockLocation') && (
                                      <td className="sticky z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('oclockLocation') }}>{row.oclockLocation}</td>
                                    )}
                                    {isColumnVisible('Documents') && (
                                      <td className="sticky z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('Documents') }}>
                                        <div className="flex items-center justify-center">
                                          {row.ndtIndicationDocuments && Array.isArray(row.ndtIndicationDocuments) && row.ndtIndicationDocuments.length > 0 ? (
                                            row.ndtIndicationDocuments.length === 1 ? (
                                              <Button
                                                variant="outline"
                                                //   size="sm"
                                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                                                title={`View ${row.ndtIndicationDocuments[0].fileName || 'Document'}`}
                                                onClick={() => handleDownloadDocument(row.ndtIndicationDocuments[0])}
                                              >
                                                <FileText className="h-4 w-4" />
                                              </Button>
                                            ) : (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={!canEdit}
                                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0"
                                                title={`View ${row.ndtIndicationDocuments.length} documents`}
                                                onClick={() => handleOpenDocuments(row.ndtIndicationDocuments)}
                                              >
                                                <FileText className="h-4 w-4" />
                                                <span className="text-xs">({row.ndtIndicationDocuments.length})</span>
                                              </Button>
                                            )
                                          ) : (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                    {isColumnVisible('actions') && (
                                      <td className="sticky z-10 bg-white px-4 text-center text-xs text-gray-600" style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                                        <div className="flex items-center justify-center gap-1">
                                          <Button
                                            variant="outline"
                                            disabled={!canEdit}
                                            //  size="sm"
                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 w-8 h-8 p-0 flex items-center justify-center"
                                            title="Edit Repair Log"
                                            onClick={() => {
                                              setEditingRow(row);
                                              setIsEditDialogOpen(true);
                                            }}
                                          >
                                            <Edit className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="outline"
                                            disabled={!canEdit}
                                            //  size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                                            title="Delete Repair Log"
                                            onClick={() => {
                                              setRepairLogToDelete(row);
                                              setIsDeleteRepairLogModalVisible(true);
                                            }}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </td>
                                    )}
                                    {isColumnVisible('defectLocationStart') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{defectLocationParts.start}</td>
                                    )}
                                    {isColumnVisible('defectLocationEnd') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{defectLocationParts.end}</td>
                                    )}
                                    {isColumnVisible('defectLength') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.defectLength}</td>
                                    )}
                                    {isColumnVisible('defectDepth') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.defectDepth}</td>
                                    )}
                                    {isColumnVisible('defectHeight') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.defectHeight}</td>
                                    )}
                                    {isColumnVisible('weldersN') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.weldersN}</td>
                                    )}
                                    {isColumnVisible('welderRepNo') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.welderRepNo}</td>
                                    )}
                                    {isColumnVisible('location') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.location}</td>
                                    )}
                                    {isColumnVisible('defectType') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.defectType}</td>
                                    )}
                                    {isColumnVisible('passName') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.passName || '-'}</td>
                                    )}
                                    {isColumnVisible('time') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.time || '-'}</td>
                                    )}
                                    {isColumnVisible('evaluation') && (
                                      <td className="px-4 text-center text-xs text-gray-600">
                                        <div className="flex items-center justify-center">
                                          {(() => {
                                            const evaluation = row.evaluation;
                                            if (evaluation === 'Accept' || evaluation === 'ACCEPT' || evaluation === 'acceptable') {
                                              return <ThumbsUp className="h-5 w-5 text-green-600" />;
                                            } else if (evaluation === 'Reject' || evaluation === 'REJECT' || evaluation === 'reject') {
                                              return <ThumbsDown className="h-5 w-5 text-red-600" />;
                                            } else {
                                              return <span className="text-gray-400">-</span>;
                                            }
                                          })()}
                                        </div>
                                      </td>
                                    )}
                                    {isColumnVisible('criticalWeld') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.criticalWeld || '-'}</td>
                                    )}
                                    {isColumnVisible('repair') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.repair}</td>
                                    )}
                                    {isColumnVisible('comments') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.comments}</td>
                                    )}
                                    {isColumnVisible('correctiveActions') && (
                                      <td className="px-4 text-left text-xs text-gray-600" style={{ maxWidth: '400px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                        <div style={{ maxWidth: '90ch' }}>
                                          {row.correctiveActions || '-'}
                                        </div>
                                      </td>
                                    )}
                                    {isColumnVisible('acceptedWorkmanship') && (
                                      <td className="px-4 text-center text-xs text-gray-600">
                                        <div className="flex items-center justify-center">
                                          {(() => {
                                            const workmanship = row.acceptedWorkmanship;
                                            if (workmanship === 'Yes' || workmanship === 'YES' || workmanship === true || workmanship === 'TRUE' || workmanship === 'true') {
                                              return <ThumbsUp className="h-5 w-5 text-green-600" />;
                                            } else if (workmanship === 'No' || workmanship === 'NO' || workmanship === false || workmanship === 'FALSE' || workmanship === 'false') {
                                              return <ThumbsDown className="h-5 w-5 text-red-600" />;
                                            } else {
                                              return <span className="text-gray-400">-</span>;
                                            }
                                          })()}
                                        </div>
                                      </td>
                                    )}
                                    {isColumnVisible('rca') && (
                                      <td className="px-4 text-center text-xs text-gray-600">{row.rca}</td>
                                    )}
                                    {isColumnVisible('rootCause') && (
                                      <td className="px-4 text-left text-xs text-gray-600" style={{ maxWidth: '600px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                        <div style={{ maxWidth: '90ch' }}>
                                          {row.rootCause}
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                        </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Document Tab */}
              <TabsContent 
                value="document" 
                className="flex-1 m-0 p-0 data-[state=active]:flex data-[state=inactive]:hidden flex-col"
                style={{ height: 'calc(100% - 72px)' }}
              >
                <div className="flex-1 relative">
                  <div 
                    className="absolute inset-0 overflow-x-auto overflow-y-auto pb-20 scrollbar-thin"
                    style={{
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehaviorX: 'contain'
                    }}
                  >
                    <div className="bg-white border border-gray-200">
                    <table className="border-collapse w-full" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
                      <thead className="sticky top-0 bg-white z-30">
                        <tr className="border-b h-[72px] bg-gray-50">
                          <th className="sticky left-0 z-20 bg-gray-50 min-w-[60px] w-[92px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                            <div className="flex items-center justify-center gap-2">
                              <Hash className="h-3 w-3" />
                              <span className="text-xs">Sno</span>
                            </div>
                          </th>
                          <th className="px-4 text-left text-xs font-medium text-gray-600 min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors">
                            <div className="flex items-center gap-2">
                              <FileText className="h-3 w-3" />
                              <span className="text-xs">File Name</span>
                            </div>
                          </th>
                          <th className="px-4 text-left text-xs font-medium text-gray-600 min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors">
                            <div className="flex items-center gap-2">
                              <FileText className="h-3 w-3" />
                              <span className="text-xs">Doc Type</span>
                            </div>
                          </th>
                          <th className="px-4 text-center text-xs font-medium text-gray-600 min-w-[120px]">
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs">Actions</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {ndtRepairLogDocsLoading ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                              Loading documents...
                            </td>
                          </tr>
                        ) : documentData[0]?.ndtIndicationDocuments?.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                              No documents available
                            </td>
                          </tr>
                        ) : (
                          documentData[0]?.ndtIndicationDocuments?.map((doc: any, index: number) => (
                            <tr key={doc.id} className="h-[52px] bg-white hover:bg-gray-50 transition-colors border-b border-gray-200">
                              <td className="sticky z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: 0 }}>
                                {index + 1}
                              </td>
                              <td className="px-4 text-left text-xs text-gray-600">
                                {doc.file_name}
                              </td>
                              <td className="px-4 text-left text-xs text-gray-600">
                                {(() => {
                                  // Get tags from document - could be tags, tagsArray, or tagIds
                                  const docTagIds = doc.tags || doc.tagsArray || doc.tagIds || [];
                                  if (!Array.isArray(docTagIds) || docTagIds.length === 0) {
                                    return doc.docType || '-';
                                  }

                                  // Filter NDT tags and match with document tag IDs
                                  const tagType = SPEC_TAG_TYPES['NDT'];
                                  const ndtTags = localDocumentTags.filter((tag: any) => tag.tags_type === tagType);

                                  // Match tag IDs and get tag names
                                  const matchedTags = ndtTags
                                    .filter((tag: any) => {
                                      const tagId = tag.tags_id;
                                      return docTagIds.some((docTagId: any) => {
                                        // Handle both number and string comparisons
                                        return Number(docTagId) === Number(tagId) || String(docTagId) === String(tagId);
                                      });
                                    })
                                    .map((tag: any) => tag.tags_name);

                                  return matchedTags.length > 0 ? matchedTags.join(', ') : (doc.docType || '-');
                                })()}
                              </td>
                              <td className="px-4 text-center text-xs text-gray-600">
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    variant="outline"
                                    // size="sm"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 w-8 h-8 p-0 flex items-center justify-center"
                                    title={`Download ${doc.file_name}`}
                                    onClick={() => handleDownloadDocument(doc)}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    disabled={!canEdit}
                                    // size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                                    title={`Delete ${doc.file_name}`}
                                    onClick={() => handleDeleteDocument(doc)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>

      {/* Add Repair Log Dialog */}
      <AddRepairLogDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        weld={weld}
        weldNumber={weld.weldNumber || (weld.id ? String(weld.id) : '')}
      />

      {/* Edit Repair Log Dialog */}
      {editingRow && (
        <AddRepairLogDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setEditingRow(null);
          }}
          weld={weld}
          weldNumber={weld.weldNumber || (weld.id ? String(weld.id) : '')}
          initialData={{
            id: editingRow.id,
            date: editingRow.date || '',
            shift: editingRow.shift || '',
            weldersNo: editingRow.weldersN || '',
            welderRepairNo: editingRow.welderRepNo || '',
            location: editingRow.location || '',
            defectType: editingRow.defectType || '',
            acceptedWorkmanship: editingRow.acceptedWorkmanship as 'Yes' | 'No' | undefined,
            defectLocationStart: parseDefectLocation(editingRow.defectLocation).start,
            defectLocationEnd: parseDefectLocation(editingRow.defectLocation).end,
            defectLength: editingRow.defectLength?.replace(' mm', '') || '',
            defectDepth: editingRow.defectDepth?.replace(' mm', '') || '',
            defectHeight: editingRow.defectHeight?.replace(' mm', '') || '',
            comments: editingRow.comments || '',
            rootCause: editingRow.rootCause || '',
            ndtIndicationDocId: editingRow.ndtIndicationDocId || [],
            ndtIndicationDocuments: editingRow.ndtIndicationDocuments || [],
            documents: [],
            documentTags: [],
            pipeDia: (weld as any).pipeDiameter || (weld as any).diameter || 20,
            oclockLocation: editingRow.oclockLocation || '',
            repair: editingRow.repair || '',
            rca: editingRow.rca || '',
            passName: editingRow.passName || '',
            evaluation: editingRow.evaluation || '',
            criticalWeld: editingRow.criticalWeld || '',
            correctiveActions: editingRow.correctiveActions || '',
            time: editingRow.time || ''
          }}
        />
      )}

      {/* Column Selector Modal */}
      <ColumnSelectorModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        onSave={handleColumnsSave}
        columns={NDT_REPAIR_LOG_COLUMNS}
        selectedColumns={visibleColumns}
        title="Select Columns - NDT Repair Log"
        defaultColumns={getAllColumnKeys(NDT_REPAIR_LOG_COLUMNS)}
      />

      {/* Document Upload Modal */}
      {isUploadDocumentModalOpen && (
        <DocumentUploadModal
          isOpen={isUploadDocumentModalOpen}
          onClose={() => setIsUploadDocumentModalOpen(false)}
          onUpload={handleUpload}
          title="Upload NDT Indication Log Document"
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={100}
          buttonText="Select Document"
          label="Document"
          description="Upload NDT indication log documents (PDF, Excel, DOC, etc...)"
          spec={{
            type: 'NDT',
            pipeId: weld.selectedPipe?.pipeId ? Number(weld.selectedPipe?.pipeId) : null,
            projectId: weld.projectId ? Number(weld.projectId) : null,
            projectTypeId: weld.selectedPipe?.projectTypeId ? Number(weld.selectedPipe?.projectTypeId) : null,
            uploadDate: new Date().toISOString()
          } as any}
          documentType="NDT"
        />
      )}

      {/* Document Download Modal */}
      {selectedDocuments && selectedDocuments.length > 0 && (
        <DocumentDownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => {
            setIsDownloadModalOpen(false);
            setSelectedDocuments([]);
          }}
          documents={selectedDocuments}
          onDownload={(doc: any) => handleDownloadDocument(doc)}
          title="Download NDT Indication Documents"
        />
      )}

      {/* Delete Confirmation Dialog */}
      {documentToDelete && (
        <DeleteConfirmationDialog
          isOpen={isDeleteModalVisible}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          isLoading={deleteLoading}
          title="Delete NDT Document"
          itemType="NDT Document"
          itemName={documentToDelete?.file_name || documentToDelete?.fileName || 'Document'}
          warningMessage="This NDT document will be permanently removed from the system. This action cannot be undone."
          detailsRenderer={(item: any) => (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{item.file_name || item.fileName || 'Unknown file'}</span>
              </div>
              {item.docType && (
                <div className="text-sm text-gray-600">
                  Type: {item.docType}
                </div>
              )}
              {item.uploadDate && (
                <div className="text-sm text-gray-600">
                  Uploaded: {new Date(item.uploadDate).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
          itemToDelete={documentToDelete}
        />
      )}

      {/* Delete Repair Log Confirmation Dialog */}
      {repairLogToDelete && (
        <DeleteConfirmationDialog
          isOpen={isDeleteRepairLogModalVisible}
          onClose={() => {
            setIsDeleteRepairLogModalVisible(false);
            setRepairLogToDelete(null);
          }}
          onConfirm={async () => {
            if (repairLogToDelete && weld) {
              const weldId = typeof weld.id === 'number' ? weld.id : (weld.id ? parseInt(String(weld.id), 10) : 0);
              const projectId = weld.projectId || (weld as any).project?.projectId;
              const pipeId = weld.selectedPipe?.pipeId ? Number(weld.selectedPipe?.pipeId) : null;

              dispatch(deleteNDTRepairLog({
                id: repairLogToDelete.id,
                project_id: projectId,
                weld_id: weldId,
                pipe_id: pipeId
              }));
            }
          }}
          isLoading={deleteNDTRepairLogLoading}
          title="Delete Repair Log"
          itemType="Repair Log"
          itemName={`Repair Log - ${repairLogToDelete?.date || 'Unknown'}`}
          warningMessage="This repair log will be permanently removed from the system. This action cannot be undone."
          detailsRenderer={(item: any) => (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="font-medium">Date: {item.date || 'Unknown'}</span>
              </div>
              {item.defectType && (
                <div className="text-sm text-gray-600">
                  Defect Type: {item.defectType}
                </div>
              )}
              {item.shift && (
                <div className="text-sm text-gray-600">
                  Shift: {item.shift}
                </div>
              )}
            </div>
          )}
          itemToDelete={repairLogToDelete}
        />
      )}

      {/* Upload NDT Repair Log Data Dialog */}
      <UploadNDTRepairLogDataDialog
        isOpen={isUploadNDTRepairLogDataDialogOpen}
        onClose={() => setIsUploadNDTRepairLogDataDialogOpen(false)}
        uploadMode="ndt"
        onUploadComplete={() => {
          // Refresh NDT indication logs after successful upload
          const weldId = typeof weld.id === 'number' ? weld.id : (weld.id ? parseInt(String(weld.id), 10) : 0);
          const projectId = weld.projectId || (weld as any).project?.projectId;
          const pipeId = weld.selectedPipe?.pipeId ? Number(weld.selectedPipe?.pipeId) : null;

          if (projectId && weldId) {
            const payload: any = {
              project_id: projectId,
              weld_id: weldId
            };

            if (pipeId !== null && pipeId !== undefined && !isNaN(pipeId)) {
              payload.pipe_id = pipeId;
            }

            dispatch(fetchNDTIndicationLogs(payload));
          }
        }}
        weld={weld}
      />

      {/* Upload Manual Weld Data Dialog (TLOG) */}
      <UploadNDTRepairLogDataDialog
        isOpen={isUploadManualWeldDataDialogOpen}
        onClose={() => setIsUploadManualWeldDataDialogOpen(false)}
        uploadMode="manual-weld"
        onUploadComplete={() => {
          setIsUploadManualWeldDataDialogOpen(false);
        }}
        weld={weld}
      />
    </Dialog>
  );
};

